import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { ISnListResponse } from '@lib/api';
import { t } from '@lib/translation';
import { SnDialogService } from '@lib/ui/dialog';

import { SnPublicity } from '../enums';
import { SnChannel, SnChannelSettings } from '../models';

@Injectable({ providedIn: 'root' })
export class SnChannelsService {
  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
    private readonly _dialogService: SnDialogService,
  ) {}

  public getChannelList(): Observable<ISnListResponse<SnChannel>> {
    return this._httpClient
      .get('/api/communications/chats/channels/', {
        params: { excludeMembers: 'true' },
      })
      .pipe(
        map((response: any) => ({
          count: response.count,
          totalPages: response.totalPages,
          results: response.results.map((channel) => new SnChannel(channel)),
        })),
      );
  }

  public getChannel(sid: string): Observable<SnChannel> {
    return this._httpClient
      .get(`/api/communications/chats/channels/${sid}/`)
      .pipe(map((response: Object) => new SnChannel(response)));
  }

  public deleteChannel(sid: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: t('communication.chat.confirmations.deleteChannel'),
        confirmButton: {
          text: t('common.delete').toUpperCase(),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => {
          return this._httpClient.delete(`/api/communications/chats/channels/${sid}/`);
        }),
        tap({
          error: () => {
            this._toastrService.error(t('communication.chat.errors.deleteChannel'));
          },
        }),
      );
  }

  public createDirectChannel(data: any): Observable<SnChannel> {
    return data.users?.length === 1
      ? this.createPairChannel(data.users[0])
      : this.createUserChannel(data.users);
  }

  public createPairChannel(pair: string): Observable<SnChannel> {
    return this._httpClient
      .post('/api/communications/chats/channels-pair/', { pair })
      .pipe(map((response) => new SnChannel(response)));
  }

  public createUserChannel(users: string): Observable<SnChannel> {
    return this._httpClient
      .post('/api/communications/chats/channels-user/', { users })
      .pipe(map((response) => new SnChannel(response)));
  }

  public createOrganizationChannel(data: any): Observable<SnChannel> {
    const channelData = {
      users: data.users,
      publicity: data.publicity ? SnPublicity.Public : SnPublicity.Private,
      hideSystemNotifications: data.hideSystemNotifications ? true : false,
      name: data.name,
    };

    return this._httpClient
      .post('/api/communications/chats/channels-organization/', channelData)
      .pipe(map((response) => new SnChannel(response)));
  }

  public updateOrganizationChannel(sid: string, data: any, users: string[]): Observable<SnChannel> {
    const channelData = {
      users: data.users,
      name: data.name,
      publicity: data.publicity ? SnPublicity.Public : SnPublicity.Private,
      hideSystemNotifications: data.hideSystemNotifications ? true : false,
    };

    return this._httpClient
      .patch(`/api/communications/chats/channels-organization/${sid}/`, channelData)
      .pipe(
        map((response) => new SnChannel(response)),
        tap({
          next: () => {
            this._toastrService.success(t('communication.chat.messages.channelUpdated'));
          },
          error: () => {
            this._toastrService.error(t('communication.chat.errors.updateChannel'));
          },
        }),
      );
  }

  public getChannelSettings(channelId: string): Observable<SnChannelSettings> {
    return this._httpClient
      .get(`/api/communications/chats/channels/${channelId}/settings/`)
      .pipe(map((response) => new SnChannelSettings(response)));
  }

  public updateChannelSettings(channelId: string, data: any): Observable<SnChannelSettings> {
    return this._httpClient
      .patch(`/api/communications/chats/channels/${channelId}/settings/`, data)
      .pipe(
        map((response) => new SnChannelSettings(response)),
        tap({
          next: () => {
            this._toastrService.success(t('communication.chat.messages.channelSettingsUpdated'));
          },
          error: () => {
            this._toastrService.error(t('communication.chat.errors.updateChannelSettings'));
          },
        }),
      );
  }

  public toggleChannelNotification(channel: SnChannel): void {
    if (!channel.twilioChannel) {
      return;
    }

    if (channel.twilioChannel.notificationLevel === 'muted') {
      channel.twilioChannel.setUserNotificationLevel('default');
    } else {
      channel.twilioChannel.setUserNotificationLevel('muted');
    }
  }

  public exportChatChannelToPdf(channelId: string, data: any): Observable<Object> {
    return this._httpClient
      .post(`/api/communications/chats/channels/${channelId}/export/`, data)
      .pipe(
        tap({
          next: () =>
            this._toastrService.success(
              t('communication.chat.messages.exportEmailInstructions'),
              t('communication.chat.messages.channelExportedSuccessfully'),
              {
                timeOut: 6000,
                progressBar: true,
              },
            ),
          error: () =>
            this._toastrService.error(t('communication.chat.errors.exportChannel')),
        }),
      );
  }
}
